/**
 * Edit Location Modal
 * Full implementation for editing existing locations
 */

import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Label, Textarea, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Alert } from '@/components/ui';
import { updateLocation, getLocationWithPrivateDetails } from '../../services/hierarchicalLocationService';
import { AlertCircle } from 'lucide-react';
import type { Location } from '@simcheck/shared-types';

interface EditLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  location: Location;
  /**
   * Read-only view for callers who can't edit this location (shared locations
   * and other orgs' sites). The API refuses the write anyway — this stops us
   * from offering an edit form that can only ever 403.
   */
  readOnly?: boolean;
  /**
   * Back-office detail — street address, postal code, and the free-text
   * description (which carries partner/shipment notes like "Partner deployment
   * site — Annatel, Paris (shipment #1)") — is super_admin/support only. The
   * map fuzzes coordinates to city level for everyone else and the API redacts
   * these fields outright; this modal must not be the hole in that. Own-org
   * locations are the org's own data, so the fields stay whenever the caller
   * can edit.
   */
  canViewSensitiveData?: boolean;
}

export const EditLocationModal: React.FC<EditLocationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  location,
  readOnly = false,
  canViewSensitiveData = true,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [timezone, setTimezone] = useState('');
  const [type, setType] = useState<Location['type']>('datacenter');
  const [status, setStatus] = useState<Location['status']>('active');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!location || !isOpen) return;

    setDisplayName(location.displayName || location.name || '');
    setDescription(location.description || '');
    setCity(location.city || '');
    setState(location.state || '');
    setAddress(location.address || '');
    setPostalCode(location.postalCode || '');
    setTimezone(location.timezone || '');
    // Legacy docs may carry values the API no longer accepts (old 'remote'
    // type, 'inactive' status) — normalize so a save doesn't 400 on an enum.
    const validTypes: Location['type'][] = ['datacenter', 'office', 'lab', 'home', 'mobile'];
    setType(validTypes.includes(location.type) ? location.type : 'datacenter');
    const validStatuses: Location['status'][] = ['active', 'offline', 'maintenance', 'decommissioned'];
    setStatus(validStatuses.includes(location.status) ? location.status : 'active');

    // The page reads locations straight from Firestore, where owner-only detail
    // (description / address / postal code) deliberately isn't stored. Editors
    // need the real values to edit them, so fetch them from the API — it merges
    // the private subcollection back in for callers who own the site. Read-only
    // viewers must never see those fields, so we don't ask.
    if (readOnly) return;
    let cancelled = false;
    getLocationWithPrivateDetails(location.id)
      .then((full) => {
        if (cancelled) return;
        setDescription(full.description || '');
        setAddress(full.address || '');
        setPostalCode(full.postalCode || '');
      })
      .catch((err) => console.error('Failed to load location details:', err));
    return () => {
      cancelled = true;
    };
  }, [location, isOpen, readOnly]);

  // Fields are frozen in read-only mode; precise siting is hidden outright.
  const locked = loading || readOnly;
  const showSiting = !readOnly || canViewSensitiveData;

  const handleSubmit = async () => {
    if (readOnly) return;
    if (!displayName || !city) {
      setError('Display Name and City are required.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await updateLocation(location.id, {
        displayName,
        description,
        city,
        state,
        address,
        postalCode,
        timezone,
        type,
        status,
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to update location:', err);
      setError(err instanceof Error ? err.message : 'Failed to update location.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={readOnly ? 'Location Details' : 'Edit Location'}
      description={
        readOnly
          ? 'This location is managed by its owner — details are read-only.'
          : 'Update location details'
      }
    >
      <div className="space-y-4 py-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <div>{error}</div>
          </Alert>
        )}
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="edit-display-name">Display Name *</Label>
            <Input
              id="edit-display-name"
              placeholder="e.g., Berlin DC1"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={locked}
            />
          </div>
          <div>
            <Label htmlFor="edit-city">City *</Label>
            <Input
              id="edit-city"
              placeholder="e.g., Berlin"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={locked}
            />
          </div>
        </div>

        {showSiting && (
          <div>
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              placeholder="Optional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={locked}
              rows={2}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="edit-state">State/Province</Label>
            <Input
              id="edit-state"
              placeholder="e.g., NY"
              value={state}
              onChange={(e) => setState(e.target.value)}
              disabled={locked}
            />
          </div>
          {showSiting && (
            <div>
              <Label htmlFor="edit-postal-code">Postal Code</Label>
              <Input
                id="edit-postal-code"
                placeholder="e.g., 10115"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                disabled={locked}
              />
            </div>
          )}
        </div>

        {showSiting && (
          <div>
            <Label htmlFor="edit-address">Address</Label>
            <Input
              id="edit-address"
              placeholder="Street address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={locked}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="edit-timezone">Timezone</Label>
            <Input
              id="edit-timezone"
              placeholder="e.g., Europe/Berlin"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={locked}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-type">Location Type</Label>
            <Select
              value={type}
              onValueChange={(value) => setType(value as Location['type'])}
              disabled={locked}
            >
              <SelectTrigger id="edit-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="datacenter">Data Center</SelectItem>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="lab">Lab</SelectItem>
                <SelectItem value="home">Home</SelectItem>
                <SelectItem value="mobile">Mobile</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-status">Status</Label>
          <Select
            value={status}
            onValueChange={(value) => setStatus(value as Location['status'])}
            disabled={locked}
          >
            <SelectTrigger id="edit-status">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
              <SelectItem value="decommissioned">Decommissioned</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="flex justify-end space-x-2 mt-6">
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button onClick={handleSubmit} disabled={loading || !displayName || !city}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>
    </Modal>
  );
};

